import type { PrivacyBits, RegionBits, RegionID, Settings } from '../../types'
import { SettingsType } from '../constants/enums'
import { PRIVACY_VALUES } from '../constants/privacy'
import {
    UEOPT_HOME_DISPLAY_REGIONS,
    UEOPT_HOME_FEED_ANIMATION_RATE_SPEEDS,
    UEOPT_MYWEBCAM_OPTIONS,
    UEOPT_ROOM_INLINE_PM_STATE,
} from './ueopts'

export const cam2camSettingsDictionary = {
    everyone: UEOPT_MYWEBCAM_OPTIONS.everyone.param,
    onlyModels: UEOPT_MYWEBCAM_OPTIONS.onlyModels.arg,
    onlyFriendsList: UEOPT_MYWEBCAM_OPTIONS.onlyFriendsList.param,
    onlyFriendsAndModels: UEOPT_MYWEBCAM_OPTIONS.onlyFriendsListAndModels.param,
    onlyAllowedViewers: UEOPT_MYWEBCAM_OPTIONS.onlyAllowedViewers.param,
    onlySpecifiedFriend: UEOPT_MYWEBCAM_OPTIONS.onlySpecifiedFriend.param,
} as const

export const inlinePmSettingsDictionary = {
    onlyChatCommand: UEOPT_ROOM_INLINE_PM_STATE.onlyChatCommand.arg,
    onlyRoomModel: UEOPT_ROOM_INLINE_PM_STATE.onlyRoomModel.param,
    all: UEOPT_ROOM_INLINE_PM_STATE.all.param,
} as const

export const privateMessagePrivacyDictionary = {
    [PRIVACY_VALUES.NO_MESSAGES]: 'None',
    [PRIVACY_VALUES.BASIC_MEMBERS]: 'Basic Members',
    [PRIVACY_VALUES.BOOKMARKS]: 'Bookmarked Models',
    [PRIVACY_VALUES.FRIENDS]: 'Friends',
    [PRIVACY_VALUES.MODELS]: 'Models',
    [PRIVACY_VALUES.PREMIUM_MEMBERS]: 'Premium Members',
    [PRIVACY_VALUES.TOP_FRIENDS]: 'Top Friends',
} as const

export const previewAnimationSpeeds = [
    UEOPT_HOME_FEED_ANIMATION_RATE_SPEEDS.static.param,
    UEOPT_HOME_FEED_ANIMATION_RATE_SPEEDS.slow.param,
    UEOPT_HOME_FEED_ANIMATION_RATE_SPEEDS.medium.param,
    UEOPT_HOME_FEED_ANIMATION_RATE_SPEEDS.fast.param,
] as const

export const getTimeoutFromAnimationUeopt = (value: number): number => {
    if (value === UEOPT_HOME_FEED_ANIMATION_RATE_SPEEDS.static.param)
        return 60 * 1000
    if (value === UEOPT_HOME_FEED_ANIMATION_RATE_SPEEDS.slow.param)
        return 15 * 1000
    if (value === UEOPT_HOME_FEED_ANIMATION_RATE_SPEEDS.medium.param)
        return 5 * 1000
    if (value === UEOPT_HOME_FEED_ANIMATION_RATE_SPEEDS.fast.param)
        return 1 * 1000

    return 15 * 1000
}

export const getAnimationsEnabledfromUeopt = (value: number): boolean =>
    value === 1 ? true : false

/**
 * app.mfc only supports 4 of the 9 available ueopt values
 * https://gitlab.ord.metapeer.com/mfc/mfc-www-react/-/issues/745
 * @param value
 */
export const getModelAnimationDisplayRate = (
    value: Settings[SettingsType.DEFAULT]['animationSpeed'],
) =>
    previewAnimationSpeeds.includes(value) === false
        ? UEOPT_HOME_FEED_ANIMATION_RATE_SPEEDS.medium.param
        : value

export const getInlinePmSettingFromUeopt = (value: number) => {
    if (value === UEOPT_ROOM_INLINE_PM_STATE.onlyChatCommand.arg) {
        return 'onlyChatCommand'
    } else if (value === UEOPT_ROOM_INLINE_PM_STATE.onlyRoomModel.param) {
        return 'onlyRoomModel'
    } else if (value === UEOPT_ROOM_INLINE_PM_STATE.all.param) {
        return 'all'
    }
}

const getPrivacyBits = (withNone?: boolean) => {
    const settings = Object.keys(privateMessagePrivacyDictionary).map(
        Number,
    ) as Array<PrivacyBits>
    if (withNone) settings.shift()
    return settings
}

export const getUserPMPrivacyBitsForDisplay = () =>
    [
        PRIVACY_VALUES.FRIENDS,
        PRIVACY_VALUES.MODELS,
        PRIVACY_VALUES.PREMIUM_MEMBERS,
        PRIVACY_VALUES.BASIC_MEMBERS,
        PRIVACY_VALUES.BOOKMARKS,
    ] as const

export const getModelPMPrivacyBitsForDisplay = () =>
    [
        PRIVACY_VALUES.FRIENDS,
        PRIVACY_VALUES.TOP_FRIENDS,
        PRIVACY_VALUES.MODELS,
        PRIVACY_VALUES.PREMIUM_MEMBERS,
        PRIVACY_VALUES.BASIC_MEMBERS,
    ] as const

export type ValidRegionBits = Exclude<RegionBits, 0>

export const getRegionBits = () => {
    const regions = Object.keys(UEOPT_HOME_DISPLAY_REGIONS) as Array<RegionID>
    const regionBits = regions.map((key) => {
        return UEOPT_HOME_DISPLAY_REGIONS[key].bit
    })
    // remove the 'all' bit (0)
    regionBits.shift()
    return regionBits as Array<ValidRegionBits>
}

/**  Calculate included regions from sum */
export const calculateRegion = (
    value: number,
): Array<ValidRegionBits | 255> => {
    const base = 255
    const regionBits = getRegionBits()
    // this is "all"
    if (regionBits[0] - base === 0) return [base]

    // how many bits do not add up to the value returned?
    // these are our region combinations
    // if we get undefined, all regions are included so get them
    const discluddedRegions =
        getSubsetFromSum(regionBits, value) || getRegionBits()

    const regions = getIncludedRegions(discluddedRegions)
    return regions
}

export const calculatePrivacySettings = (value: number) => {
    // if we detect we only have the base value then abort before calc
    if (value === 0 || value === PRIVACY_VALUES.NO_MESSAGES)
        return [PRIVACY_VALUES.NO_MESSAGES]

    const settingBits = getPrivacyBits()

    const settings = getSubsetFromSum(settingBits, value)
    return settings
}

/** Provides the region key code for any bit given from the FCS */
export const translateRegion = (value: ValidRegionBits | 255) => {
    const homeDisplayRegions = Object.keys(
        UEOPT_HOME_DISPLAY_REGIONS,
    ) as Array<RegionID>
    const regions = homeDisplayRegions.filter((region) => {
        const bit = UEOPT_HOME_DISPLAY_REGIONS[region].bit
        if (bit === value) return true
        else return false
    })
    return regions[0]
}

/** Returns an array of regions based on the bit that the chat server sends over */
const getIncludedRegions = (discludedRegions: Array<ValidRegionBits>) => {
    const regionBits = getRegionBits()
    const result: Array<ValidRegionBits> = []

    regionBits.forEach((bit) => {
        if (discludedRegions.indexOf(bit) < 0) result.push(bit)
    })

    return result
}

/**
 * Obtain a subset of numbers that DO NOT equal in combination to a sum
 * @param array Array to reduce
 * @param sum sum to compare against
 */
const getSubsetFromSum = <T extends number>(array: Array<T>, sum: number) => {
    const iter = (index: number, temp: Array<T>, s: number) => {
        if (s === sum) return result.push(temp.slice())
        if (index >= array.length) return
        iter(index + 1, temp.concat(array[index]), s + array[index])
        iter(index + 1, temp, s)
    }

    const result: Array<Array<T>> = []
    iter(0, [], 0)
    return result[0]
}

/** Do the math for the region code for FCS */
export const getFCSRegionCode = (
    value: number,
    currentSetting: number,
    remove: boolean,
) => {
    if (!remove) return currentSetting + value
    else return currentSetting - value
}

export const getPrivateMessagePrivacyCode = (
    currentBitCode: number,
    newBitCode: PrivacyBits,
) => {
    const currentSettings = calculatePrivacySettings(currentBitCode)
    const currentBitValue = currentBitCode === 8 ? 0 : currentBitCode
    if (currentSettings.includes(newBitCode)) {
        const nextValue = currentBitValue - newBitCode
        return nextValue as PrivacyBits
    } else {
        const nextValue = currentBitValue + newBitCode
        return nextValue as PrivacyBits
    }
}

export const checkPrivateMessagePrivacyCode = (
    currentBitCode = 0,
    checkBitCode: PrivacyBits,
): boolean => {
    return checkBitCode === (checkBitCode & currentBitCode)
}
